import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ethers } from "ethers";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS } from "../constants";
import { useTranslation } from "../hooks/useTranslation";

const PAGE_SIZE = 10;

function parseRecord(r) {
    const buyer = r?.buyer ?? r?.[0];
    const amount = r?.amount ?? r?.[1];
    const level = r?.level ?? r?.[2];
    const rank = r?.rank ?? r?.[3];
    const timestamp = r?.timestamp ?? r?.[4];
    return {
        buyer: buyer && typeof buyer === "string" ? buyer : (buyer?.toString?.() ?? ""),
        amount: amount != null ? (typeof amount === "bigint" ? amount.toString() : String(amount?.toString?.() ?? "0")) : "0",
        level: level != null ? (typeof level === "bigint" ? Number(level) : (level?.toString ? Number(level) : level)) : 0,
        rank: rank != null ? (typeof rank === "bigint" ? Number(rank) : (rank?.toString ? Number(rank) : rank)) : 0,
        timestamp: timestamp != null ? (typeof timestamp === "bigint" ? Number(timestamp) : (timestamp?.toString ? Number(timestamp) : timestamp)) : 0,
    };
}

export default function BonusHistory() {
    const navigate = useNavigate();
    const { account, isConnected } = useWallet();
    const { executeWithFailover } = useRPCProvider();
    const { t } = useTranslation();

    const targetAddress = account;

    const [allRecords, setAllRecords] = useState([]);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [timeRange, setTimeRange] = useState("All");
    const [levelFilter, setLevelFilter] = useState("All");

    const formatBonusAmount = useCallback((val) => {
        if (val == null) return "0.00";
        const raw = typeof val === "bigint" ? val.toString() : (val?.toString ? val.toString() : String(val));
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed)) return "0.00";
        return (parsed / 1e18).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, []);

    const formatTimestamp = useCallback((ts) => {
        if (!ts || ts === 0) return "-";
        const d = new Date(ts * 1000);
        return d.toLocaleString("en-GB");
    }, []);

    const truncateAddress = useCallback((addr) => {
        if (!addr || addr.length <= 16) return addr || "";
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    }, []);

    const fetchAllBonusRecords = useCallback(async () => {
        if (!targetAddress || !executeWithFailover) return;
        setIsLoading(true);
        try {
            const recs = await executeWithFailover(async (provider) => {
                const bonusContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.Bonus,
                    CONTRACT_ABIS.BONUS,
                    provider
                );
                const records = await bonusContract.getAllBonusRecords(targetAddress);
                const arr = records?.toArray?.() ?? (Array.isArray(records) ? records : Array.from(records ?? []));
                return arr.map(parseRecord);
            });
            setAllRecords(recs || []);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA") {
                console.warn("getAllBonusRecords failed:", e);
            }
            setAllRecords([]);
        } finally {
            setIsLoading(false);
        }
    }, [targetAddress, executeWithFailover]);

    const totalBonus = useMemo(() => {
        if (!allRecords || allRecords.length === 0) return null;
        let sum = 0n;
        for (const r of allRecords) {
            const amt = r.amount;
            // eslint-disable-next-line no-undef -- BigInt is a standard global (ES2020)
            sum += typeof amt === "bigint" ? amt : BigInt(amt?.toString?.() ?? "0");
        }
        return sum.toString();
    }, [allRecords]);

    const getTimeRangeBoundary = useCallback(() => {
        if (timeRange === "All") return 0;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
        switch (timeRange) {
            case "Today": return startOfToday;
            case "1W": return startOfToday - 7 * 86400;
            case "1M": return startOfToday - 30 * 86400;
            case "3M": return startOfToday - 90 * 86400;
            default: return 0;
        }
    }, [timeRange]);

    const filteredRecords = useMemo(() => {
        const minTs = getTimeRangeBoundary();
        let list = allRecords;
        if (minTs > 0) {
            list = list.filter((r) => r.timestamp >= minTs);
        }
        if (levelFilter !== "All") {
            const levelNum = parseInt(levelFilter.replace("S", ""), 10);
            if (!Number.isNaN(levelNum)) {
                list = list.filter((r) => r.level === levelNum);
            }
        }
        return list;
    }, [allRecords, levelFilter, getTimeRangeBoundary]);

    const totalCount = filteredRecords.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const displayRecords = useMemo(() => {
        const start = page * PAGE_SIZE;
        return filteredRecords.slice(start, start + PAGE_SIZE);
    }, [filteredRecords, page]);

    useEffect(() => {
        fetchAllBonusRecords();
    }, [fetchAllBonusRecords]);

    useEffect(() => {
        setPage(0);
    }, [timeRange, levelFilter]);

    const handleResetFilter = () => {
        setTimeRange("All");
        setLevelFilter("All");
    };

    const handleDoneFilter = () => {
        setShowFilter(false);
    };
    const hasNext = page < totalPages - 1;
    const hasPrev = page > 0;

    const handleBack = () => {
        navigate("/community");
    };

    if (!isConnected) {
        return (
            <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10" />
                <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto">
                    <button onClick={() => navigate("/community")} className="flex items-center gap-2 text-white/80 hover:text-white mb-6">
                        <span className="material-symbols-outlined">arrow_back</span>
                        {t("common.back")}
                    </button>
                    <p className="text-white/70">{t("community.connectWalletFirst")}</p>
                </div>
            </div>
        );
    }

    if (!account) {
        return (
            <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10" />
                <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto">
                    <button onClick={() => navigate("/community")} className="flex items-center gap-2 text-white/80 hover:text-white mb-6">
                        <span className="material-symbols-outlined">arrow_back</span>
                        {t("common.back")}
                    </button>
                    <p className="text-white/70">{t("community.connectWalletFirst")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10" />
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none -z-10"
                style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                    backgroundSize: "40px 40px",
                }}
            />
            <div
                className="absolute inset-0 opacity-[0.02] pointer-events-none -z-10"
                style={{
                    backgroundImage: `linear-gradient(45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%)`,
                    backgroundSize: "60px 60px",
                }}
            />

            <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleBack}
                        className="flex items-center justify-center size-10 rounded-full text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.goBack")}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <h1 className="text-2xl font-semibold text-white">{t("community.bonusHistory") || "Bonus History"}</h1>
                </div>

                <div className="text-[#ad90cb] text-sm">
                    {t("community.totalBonus") || "Total Bonus"}:{" "}
                    <span className="text-[#DFD2FE] font-semibold">
                        {totalBonus != null ? formatBonusAmount(totalBonus) : "0.00"} VUSDT
                    </span>
                </div>

                <div className="rounded-3xl bg-[#1a1021]/70 backdrop-blur-md border border-[#9770DD]/30 p-5 shadow-lg shadow-[#9770DD]/15">
                    <div className="flex items-center justify-between mb-5">
                        <span className="text-white font-medium">{t("community.bonusHistory") || "Bonus History"}</span>
                        <button
                            onClick={() => setShowFilter(true)}
                            className="size-10 rounded-lg bg-[#241630]/60 border border-[#9770DD]/20 hover:bg-[#2d1b3d]/60 transition-colors flex items-center justify-center"
                                            aria-label={t("common.filterLabel")}
                        >
                            <span className="material-symbols-outlined text-[#9770DD] text-lg">filter_list</span>
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#4d3168]">
                                    <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                        {t("community.dateTime") || "Date & Time"}
                                    </th>
                                    <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                        {t("community.buyer") || "Buyer"}
                                    </th>
                                    <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                        {t("community.amount") || "Amount"}
                                    </th>
                                    <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                        {t("community.level") || "Level"}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={4} className="py-8 text-center text-[#ad90cb]">
                                            {t("community.loadingCommunityData")}
                                        </td>
                                    </tr>
                                ) : displayRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="py-8 text-center text-[#ad90cb]">
                                            {t("community.noBonusRecords") || "No bonus records found"}
                                        </td>
                                    </tr>
                                ) : (
                                    displayRecords.map((r, idx) => (
                                        <tr
                                            key={`${r.buyer}-${r.timestamp}-${idx}`}
                                            className="border-b border-[#4d3168]/50 last:border-0"
                                        >
                                            <td className="py-3 px-2 text-white text-xs">{formatTimestamp(r.timestamp)}</td>
                                            <td className="py-3 px-2 text-white text-xs">{truncateAddress(r.buyer)}</td>
                                            <td className="py-3 px-2 text-[#DFD2FE] font-medium">
                                                {formatBonusAmount(r.amount)} VUSDT
                                            </td>
                                            <td className="py-3 px-2 text-white text-xs">L{r.level}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#4d3168]">
                            <button
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={!hasPrev}
                                className="px-4 py-2 rounded-xl bg-[#241630]/60 border border-[#9770DD]/20 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2d1b3d]/60"
                            >
                                {t("common.prev") || "Prev"}
                            </button>
                            <span className="text-[#ad90cb] text-sm">
                                {t("history.page") || "Page"} {page + 1} {t("history.of") || "of"} {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                disabled={!hasNext}
                                className="px-4 py-2 rounded-xl bg-[#241630]/60 border border-[#9770DD]/20 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2d1b3d]/60"
                            >
                                {t("common.next") || "Next"}
                            </button>
                        </div>
                    )}
                </div>

            </div>

            <AnimatePresence>
                {showFilter && (
                    <div className="fixed inset-0 z-[100] pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowFilter(false)}
                            className="absolute inset-0 bg-gray-950/50 pointer-events-auto"
                        />
                        <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                            <motion.div
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                className="w-full max-w-md bg-[#0a0610] rounded-t-3xl border-t border-[#9770DD]/30 shadow-2xl pointer-events-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex justify-center pt-3 pb-2">
                                    <div className="w-12 h-1 bg-gray-600 rounded-full" />
                                </div>
                                <div className="px-5 pb-6 pt-2">
                                    <h2 className="text-2xl font-semibold text-white mb-6">{t("common.filter") || "Filter"}</h2>
                                    <div className="mb-6">
                                        <h3 className="text-white text-base font-medium mb-4">{t("community.timeRange") || "Time Range"}</h3>
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            {["All", "Today", "1W", "1M", "3M"].map((range) => (
                                                <button
                                                    key={range}
                                                    onClick={() => setTimeRange(range)}
                                                    className={`h-12 rounded-xl font-semibold text-sm transition-all ${timeRange === range
                                                        ? "bg-[#9770DD] text-white shadow-lg shadow-[#9770DD]/30"
                                                        : "bg-[#241630]/60 text-white border border-[#9770DD]/20 hover:border-[#9770DD]/40"
                                                    }`}
                                                >
                                                    {t(`history.time${range === "All" ? "All" : range === "Today" ? "Today" : range === "1W" ? "1W" : range === "1M" ? "1M" : "3M"}`) || range}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mb-6">
                                        <h3 className="text-white text-base font-medium mb-4">{t("community.level") || "Level"}</h3>
                                        <div className="grid grid-cols-4 gap-2">
                                            {["All", "L0", "L1", "L2", "L3", "L4", "L5", "L6", "L7", "L8", "L9", "L10"].map((lvl) => (
                                                <button
                                                    key={lvl}
                                                    onClick={() => setLevelFilter(lvl)}
                                                    className={`h-10 rounded-xl font-semibold text-xs transition-all ${levelFilter === lvl
                                                        ? "bg-[#9770DD] text-white shadow-lg shadow-[#9770DD]/30"
                                                        : "bg-[#241630]/60 text-white border border-[#9770DD]/20 hover:border-[#9770DD]/40"
                                                    }`}
                                                >
                                                    {lvl}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-3 mt-8 mb-4">
                                        <button
                                            onClick={handleResetFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#241630]/60 border border-[#9770DD]/20 text-white font-bold text-lg hover:bg-[#2d1b3d]/60 hover:border-[#9770DD]/40 transition-all"
                                        >
                                            {t("common.reset") || "Reset"}
                                        </button>
                                        <button
                                            onClick={handleDoneFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#9770DD] text-white font-bold text-lg hover:bg-[#7a1fd6] transition-all shadow-lg shadow-[#9770DD]/30"
                                        >
                                            {t("common.done") || "Done"}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
